import React, { useState, useEffect, useRef } from 'react';
import StepLayout from '../components/StepLayout';
import { api } from '../api';
import { ID_BADGE_ENABLED } from '../utils/featureFlags';

// iter-26: rebuild per Consent and Agreement Page UI Spec.
//
// Layout (left-aligned):
//   1. Heading "Consent and Agreement *" (red asterisk)
//   2. Statement paragraph
//   3. "Read full agreement here" (blue underline link)
//   4. Single orange "Yes, I consent" button (16/32 padding) + check
//      icon. NO separate Next.
//   5. Helper line "Tapping above confirms..."
//   6. Trustpilot below at 3rem margin (StepLayout default)
//
// 2026-06-26: link line now surfaces BOTH the full agreement and the
// Privacy Policy. Both are self-contained static pages shipped in the
// build under public/legal/*.html (CRA copies public/ -> build/). The
// Amplify SPA rule is `/<*>` -> /index.html with status `404-200` — a
// *conditional* fallback that fires ONLY when the path 404s, so a file
// that exists in the build (like /static/*.js, and these .html files) is
// served directly with 200. The old `/legal/agreement` (extensionless)
// had no backing file, so the fallback served the SPA — the dead link;
// pointing at real `.html` files fixes it. Confirmed live post-deploy by
// curling the URLs (must return the policy HTML, not the wizard).
const FULL_AGREEMENT_URL = '/legal/agreement.html';
const PRIVACY_POLICY_URL = '/legal/privacy.html';

export default function Step13Consent({ sessionId, updateData, onNext }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dispatchedRef = useRef(false);

  // B-IDBADGE-TOGGLE (2026-05-29): belt-and-suspenders email-change
  // dispatch when the ID-badge sub-flow is OFF. On the V3 production path
  // email-change auto-chains server-side on login success, so this is a
  // guaranteed idempotent no-op there (identity_confirm returns early once
  // an email-change step-log exists). But on the legacy non-V3 path the
  // ONLY trigger for email-change was Step09's identity_confirm — which is
  // skipped when the badge flow is off. Consent (Step13) is the first
  // operator-driven screen reached after the now-skipped badge cluster, so
  // we fire it here, on mount, DECOUPLED from the consent POST (it must not
  // depend on updateConsent succeeding). Fires once per mount.
  useEffect(() => {
    if (ID_BADGE_ENABLED) return; // ON path: Step09 already owns dispatch.
    if (!sessionId) return;
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;
    (async () => {
      try { await api.identityConfirm(sessionId); } catch (_) { /* non-fatal — idempotent / saga retry */ }
    })();
  }, [sessionId]);

  const handleConsent = async () => {
    setLoading(true);
    setError('');
    try {
      // iter-35: BE schema expects consentApplication + consentTos
      // booleans (separate columns on takeover_sessions for legal
      // evidence — server stamps consent_application_at /
      // consent_tos_at on PATCH). Step14 ToS screen was deleted in
      // iter-26, so the single "Yes, I consent" click here covers
      // both: tapping the button = agree to application terms AND
      // the linked full agreement.
      await api.updateConsent(sessionId, {
        consentApplication: true,
        consentTos: true,
      });
      updateData({ consent: true, consentApplication: true, consentTos: true });
      onNext();
    } catch (err) {
      setError(err.message || 'Failed to record consent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StepLayout title="Consent and Agreement *">
      <div className="consent-page">
        <p className="consent-statement">
          I consent to the terms below and allow Social Signal to use the LinkedIn
          account for lead generation purposes.
        </p>

        <p className="consent-link-line">
          Read our{' '}
          <a
            href={FULL_AGREEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="consent-link"
          >
            full agreement
          </a>{' '}
          and{' '}
          <a
            href={PRIVACY_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="consent-link"
          >
            Privacy Policy
          </a>
          .
        </p>

        {error && <p className="form-field__error">{error}</p>}

        <button
          type="button"
          onClick={handleConsent}
          disabled={loading}
          className="primary-cta consent-cta"
        >
          <span className="consent-cta__check" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20">
              <path d="M5 12 L10 17 L19 7" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {loading ? 'Saving...' : 'Yes, I consent'}
        </button>

        <p className="consent-helper">
          Tapping above confirms you agree to the terms above, our full agreement and Privacy Policy, and continues to the next step.
        </p>
      </div>
    </StepLayout>
  );
}
